import React, { useState, useEffect, useCallback, useMemo } from "react";
import { Col, Container } from "reactstrap";
import { approveDeclineBundle, getBundles } from "utils/api";
import { defaultNgiErrorMethod, successMessage } from "components/Common/responses/message";
import NGITable from "components/Common/NGITable";
import { FILTER_PAGE, USER_ROLES } from "utils/constants";
import useStoreFilters from "customHooks/useStoreFilters";
import { isEmpty } from "lodash";

import CustomButton from "components/Common/buttons/CustomButton";
import { makeStyles } from "@material-ui/core/styles";
import classname from "classnames";
import BundlesFilter from "pages/price-management/Bundles/bundlesFilter";
import { bundlesListColumns } from "pages/price-management/Bundles/bundlesListColumns";
import NewBundleModal from "pages/price-management/Bundles/newBundle/NewBundleModal";
import { useHistory } from "react-router";
import ConfirmationModal from "components/Common/modals/ConfirmationModal";

const useStyles = makeStyles({
  filters: { display: "flex", justifyContent: "space-between" },
  overflowText: {
    overflow: "hidden",
    maxWidth: 110,
    whiteSpace: "nowrap",
    textOverflow: "ellipsis",
  },
});

const Bundles = () => {
  const classes = useStyles();
  const history = useHistory();
  const [isLoading, setLoader] = useState(false);
  const { filters, setFilters } = useStoreFilters(FILTER_PAGE.GAS_BUNDLES);
  const [list, setList] = useState([]);
  const [isOpenCreate, setIsOpenCreate] = useState(false);
  const [approveDeclineData, setApproveDeclineData] = useState(null);

  useEffect(() => {
    handleFilter(!isEmpty(filters) ? filters : {});
  }, []);

  const handleFilter = useCallback((options = {}) => {
    setFilters(options);
    setLoader(true);
    getBundles(options)
      .then(res => {
        setList(res?.data || null);
        setLoader(false);
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, []);

  const openNewBundle = useCallback(() => setIsOpenCreate(true));
  const closeNewBundle = useCallback(() => setIsOpenCreate(false));

  const approveDecline = useCallback(
    (item, isApprove) =>
      setApproveDeclineData({
        item,
        isApprove,
      }),
    []
  );
  const approveDeclineClose = useCallback(() => setApproveDeclineData(null), []);
  const onConfirmApproveDecline = useCallback(() => {
    setLoader(true);
    approveDeclineBundle(approveDeclineData.item.id, approveDeclineData.isApprove)
      .then(() => {
        successMessage(`Bundle was ${approveDeclineData.isApprove ? "approved" : "declined"} successfully`);
        handleFilter(!isEmpty(filters) ? filters : {});
        setLoader(false);
        approveDeclineClose();
      })
      .catch(err => {
        setLoader(false);
        defaultNgiErrorMethod(err);
      });
  }, [approveDeclineData, filters]);

  const columns = useMemo(() => bundlesListColumns(classes, history, approveDecline), []);

  return (
    <>
      <div className="page-content">
        <Container fluid>
          <div className={classname(classes.filters, "filters-wrapper")}>
            <div className="filter-buttons">
              <CustomButton
                icon={<i className="bx bx-plus-circle" />}
                type="primary"
                text="NEW GAS BUNDLE"
                hideRoles={[USER_ROLES.CUSTOMER_CARE_LEADERSHIP, USER_ROLES.FINANCE]}
                onClick={openNewBundle}
              />
            </div>
            <BundlesFilter onFilter={handleFilter} isLoading={isLoading} />
          </div>
          <Col>
            <NGITable
              filtersType={FILTER_PAGE.GAS_BUNDLES}
              columns={columns}
              data={list}
              isLoading={isLoading}
              updateList={handleFilter}
            />
          </Col>
        </Container>
      </div>
      {isOpenCreate && <NewBundleModal onClose={closeNewBundle} update={handleFilter} />}

      {!!approveDeclineData && (
        <ConfirmationModal
          onCancel={approveDeclineClose}
          onConfirm={onConfirmApproveDecline}
          cancelBtnTitle="Cancel"
          confirmBtnTitle="Confirm"
          description={`Are you sure you want to do ${
            approveDeclineData.isApprove ? "approve" : "decline"
          } this bundle? \n\nPlease confirm!`}
        />
      )}
    </>
  );
};

export default Bundles;
